import {
  FETCH_SUBSCRIPTION_PLANS,
  FETCH_SUBSCRIPTION_PLANS_SUCCESS,
  FETCH_SUBSCRIPTION_PLANS_FAILED,
  FETCH_USER_SUBSCRIPTION,
  FETCH_USER_SUBSCRIPTION_SUCCESS,
  FETCH_USER_SUBSCRIPTION_FAILED,
  CREATE_SUBSCRIPTION,
  CREATE_SUBSCRIPTION_SUCCESS,
  CREATE_SUBSCRIPTION_FAILED,
  CHECK_TRIP_USAGE,
  CHECK_TRIP_USAGE_SUCCESS,
  CHECK_TRIP_USAGE_FAILED,
  FETCH_SUBSCRIPTION_STATS,
  FETCH_SUBSCRIPTION_STATS_SUCCESS,
  FETCH_SUBSCRIPTION_STATS_FAILED,
  VERIFY_OFFLINE_PAYMENT,
  VERIFY_OFFLINE_PAYMENT_SUCCESS,
  VERIFY_OFFLINE_PAYMENT_FAILED,
  CLEAR_SUBSCRIPTION_ERROR,
} from "../store/types";
import { RequestPushMsg } from "../other/NotificationFunctions";
import { fetchUser } from "./authactions";
import { firebase } from "../config/configureFirebase";
import store from "../store/store";
import { Platform } from "react-native";

// Helper function to get API base URL
const getApiBaseUrl = () => {
  try {
    const { config } = firebase;
    
    // Check if config and projectId are available
    if (!config?.projectId) {
      console.error("❌ Firebase projectId not found in config");
      return null;
    }
    
    const settings = store.getState().settingsdata.settings;

    // Check if running in React Native (mobile app)
    if (Platform.OS === "ios" || Platform.OS === "android") {
      return `https://us-central1-${config.projectId}.cloudfunctions.net`;
    }

    // For web app
    return window &&
      window.location &&
      settings.CompanyWebsite === window.location.origin
      ? window.location.origin
      : `https://${config.projectId}.web.app`;
  } catch (error) {
    console.error("❌ Error getting API base URL:", error);
    return null;
  }
};

export const fetchSubscriptionPlans =
  (userType = null, language = "en") =>
  async (dispatch, getState) => {
    dispatch({
      type: FETCH_SUBSCRIPTION_PLANS,
      payload: null,
    });
    try {
      const auth = getState().auth;
      const userId = auth && auth.profile ? auth.profile.uid : null;

      const baseUrl = getApiBaseUrl();
      
      // Check if baseUrl is available
      if (!baseUrl) {
        console.error("❌ Cannot fetch subscription plans - API base URL not available");
        dispatch({
          type: FETCH_SUBSCRIPTION_PLANS_FAILED,
          payload: "Firebase projectId not configured",
        });
        return;
      }
      
      let url = `${baseUrl}/subscription-getSubscriptionPlans?language=${language}`;
      if (userType) {
        url += `&userType=${userType}`;
      }
      if (userId) {
        url += `&userId=${userId}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();

      if (data.success) {
        dispatch({
          type: FETCH_SUBSCRIPTION_PLANS_SUCCESS,
          payload: { plans: data.plans, language: data.language },
        });
      } else {
        dispatch({
          type: FETCH_SUBSCRIPTION_PLANS_FAILED,
          payload: data.error || "Failed to fetch subscription plans",
        });
      }
    } catch (error) {
      dispatch({
        type: FETCH_SUBSCRIPTION_PLANS_FAILED,
        payload:
          error.message || "Network error while fetching subscription plans",
      });
    }
  };

export const fetchUserSubscription = (userId) => async (dispatch) => {
  dispatch({
    type: FETCH_USER_SUBSCRIPTION,
    payload: null,
  });
  try {
    const baseUrl = getApiBaseUrl();
    
    // Check if baseUrl is available
    if (!baseUrl) {
      console.error("❌ Cannot fetch user subscription - API base URL not available");
      dispatch({
        type: FETCH_USER_SUBSCRIPTION_FAILED,
        payload: "Firebase projectId not configured",
      });
      return;
    }
    
    const response = await fetch(
      `${baseUrl}/subscription-getUserSubscription?userId=${userId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const data = await response.json();

    if (data.success) {
      dispatch({
        type: FETCH_USER_SUBSCRIPTION_SUCCESS,
        payload: data.subscription,
      });
    } else {
      dispatch({
        type: FETCH_USER_SUBSCRIPTION_FAILED,
        payload: data.error || "Subscription not found",
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_USER_SUBSCRIPTION_FAILED,
      payload: error.message,
    });
  }
};

export const createSubscription = (subscriptionData) => async (dispatch) => {
  dispatch({
    type: CREATE_SUBSCRIPTION,
    payload: null,
  });
  try {
    const baseUrl = getApiBaseUrl();
    
    // Check if baseUrl is available
    if (!baseUrl) {
      console.error("❌ Cannot create subscription - API base URL not available");
      dispatch({
        type: CREATE_SUBSCRIPTION_FAILED,
        payload: "Firebase projectId not configured",
      });
      return;
    }
    
    const response = await fetch(
      `${baseUrl}/subscription-createSubscription`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(subscriptionData),
      }
    );
    const data = await response.json();

    if (data.success) {
      dispatch({
        type: CREATE_SUBSCRIPTION_SUCCESS,
        payload: data.subscription,
      });

      // Refresh user data to get updated subscription status
      if (subscriptionData.userId) {
        dispatch(fetchUser());
      }

      return {
        success: true,
        subscription: data.subscription,
        message: data.message,
      };
    } else {
      dispatch({
        type: CREATE_SUBSCRIPTION_FAILED,
        payload: data.error || "Failed to create subscription",
      });
      return { success: false, error: data.error };
    }
  } catch (error) {
    dispatch({
      type: CREATE_SUBSCRIPTION_FAILED,
      payload: error.message,
    });
    return { success: false, error: error.message };
  }
};

export const checkTripUsage =
  (userId, action = "check", userType = "customer") =>
  async (dispatch) => {
    dispatch({
      type: CHECK_TRIP_USAGE,
      payload: null,
    });
    try {
      const baseUrl = getApiBaseUrl();
      
      // Check if baseUrl is available
      if (!baseUrl) {
        console.error("❌ Cannot check trip usage - API base URL not available");
        const errorResult = {
          success: false,
          canTakeTrip: true, // Allow trip on error to prevent blocking users
          reason: "API configuration error",
          error: "Firebase projectId not configured"
        };
        
        dispatch({
          type: CHECK_TRIP_USAGE_SUCCESS,
          payload: errorResult,
        });
        
        return errorResult;
      }
      
      const response = await fetch(
        `${baseUrl}/subscription-checkTripUsage`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId, action, userType }),
        }
      );
      const data = await response.json();

      if (data.success) {
        dispatch({
          type: CHECK_TRIP_USAGE_SUCCESS,
          payload: data,
        });

        // If action was increment and limit reached, show notification
        if (action === "increment" && data.limitReached) {
          RequestPushMsg(userId, {
            title: "Subscription Limit Reached",
            msg: "Your monthly trip limit has been reached. Please upgrade your plan.",
            channelId: "general",
            sound: Platform.OS === "android" ? "general" : "general.mp3", // Correct format for each platform
          });
        }

        return data;
      } else {
        dispatch({
          type: CHECK_TRIP_USAGE_FAILED,
          payload: data.error || "Failed to check trip usage",
        });
        return data;
      }
    } catch (error) {
      dispatch({
        type: CHECK_TRIP_USAGE_FAILED,
        payload: error.message,
      });
      return { success: false, error: error.message };
    }
  };

export const fetchSubscriptionStats = () => async (dispatch) => {
  dispatch({
    type: FETCH_SUBSCRIPTION_STATS,
    payload: null,
  });
  try {
    const baseUrl = getApiBaseUrl();
    
    // Check if baseUrl is available
    if (!baseUrl) {
      console.error("❌ Cannot fetch subscription stats - API base URL not available");
      dispatch({
        type: FETCH_SUBSCRIPTION_STATS_FAILED,
        payload: "Firebase projectId not configured",
      });
      return;
    }
    
    const response = await fetch(
      `${baseUrl}/subscription-getSubscriptionStats`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const data = await response.json();

    if (data.success) {
      dispatch({
        type: FETCH_SUBSCRIPTION_STATS_SUCCESS,
        payload: data.stats,
      });
    } else {
      dispatch({
        type: FETCH_SUBSCRIPTION_STATS_FAILED,
        payload: data.error || "Failed to fetch subscription statistics",
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_SUBSCRIPTION_STATS_FAILED,
      payload: error.message,
    });
  }
};

export const verifyOfflinePayment =
  (userId, approved, adminNote) => async (dispatch) => {
    dispatch({
      type: VERIFY_OFFLINE_PAYMENT,
      payload: null,
    });
    try {
      const baseUrl = getApiBaseUrl();
      
      // Check if baseUrl is available
      if (!baseUrl) {
        console.error("❌ Cannot verify offline payment - API base URL not available");
        dispatch({
          type: VERIFY_OFFLINE_PAYMENT_FAILED,
          payload: "Firebase projectId not configured",
        });
        return;
      }
      
      const response = await fetch(
        `${baseUrl}/subscription-verifyOfflinePayment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId, approved, adminNote }),
        }
      );
      const data = await response.json();

      if (data.success) {
        dispatch({
          type: VERIFY_OFFLINE_PAYMENT_SUCCESS,
          payload: data.message,
        });

        // Send notification to user
        const notificationTitle = approved
          ? "Subscription Activated"
          : "Subscription Rejected";
        const notificationBody = approved
          ? "Your subscription has been activated successfully!"
          : "Your subscription payment was rejected. Please contact support.";

        RequestPushMsg(userId, {
          title: notificationTitle,
          msg: notificationBody,
          channelId: "general",
          sound: Platform.OS === "android" ? "general" : "general.mp3", // Correct format for each platform
        });

        return { success: true, message: data.message };
      } else {
        dispatch({
          type: VERIFY_OFFLINE_PAYMENT_FAILED,
          payload: data.error || "Failed to verify payment",
        });
        return { success: false, error: data.error };
      }
    } catch (error) {
      dispatch({
        type: VERIFY_OFFLINE_PAYMENT_FAILED,
        payload: error.message,
      });
      return { success: false, error: error.message };
    }
  };

export const clearSubscriptionError = () => (dispatch) => {
  dispatch({
    type: CLEAR_SUBSCRIPTION_ERROR,
    payload: null,
  });
};

// Helper function to generate subscription order ID for payment gateways
export const generateSubscriptionOrderId = (userId, planId) => {
  return `subscription-${userId}-${planId}-${Date.now()}`;
};

// Initialize subscription plans (for admin)
// Note: Backend function is commented out to prevent accidental data initialization
// This frontend function will show appropriate message if backend is unavailable
export const initializeSubscriptionPlans =
  (language = "en") =>
  async (dispatch) => {
    try {
      const baseUrl = getApiBaseUrl();
      
      // Check if baseUrl is available
      if (!baseUrl) {
        console.error("❌ Cannot initialize subscription plans - API base URL not available");
        return { success: false, error: "Firebase projectId not configured" };
      }
      
      const response = await fetch(
        `${baseUrl}/subscription-initSubscriptionPlans`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ language }),
        }
      );
      const data = await response.json();

      if (data.success) {
        // Refresh plans after initialization with the specified language
        dispatch(fetchSubscriptionPlans(null, language));
        return {
          success: true,
          message: data.message,
          language: data.language,
        };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return {
        success: false,
        error: "Backend initialization function is disabled for safety",
      };
    }
  };
